import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Row, Col, Container, Form, Alert, Button, Card, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { detailCategories, listCategories } from "../actions/categoryActions";
import Loader from "../components/Loader";
import Message from "../components/Message";
import CategorySection from "../components/Category/CategorySection";
import ProductByCategory from "../components/Category/ProductByCategory";
import { listProducts } from "../actions/productActions";
import FilterByBrand from "../components/Category/FilterByBrand";
import FilterByStatus from "../components/Category/FilterByStatus";
import FilterContext from "../contexts/FilterContext";
import { FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { logout } from '../actions/userActions';
import { COLORS } from '../constants/colors';

function CategoryScreen() {
  const { categoryParams } = useParams();
  const [slug, id] = categoryParams ? categoryParams.split("-c-") : ['', ''];
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    categoryReducers: { categoryDetailsLoading, categoryDetailsError, category, categories }
  } = useSelector((state) => state)

  const [searchParams] = useSearchParams()
  const keyword = searchParams.get('search')

  const [selectedBrands, setSelectedBrands] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('1');
  const [selectedOrdering, setSelectedOrdering] = useState('endDate');

  const {
    productReducers: { products, loading: productsLoading, error: productsError }
  } = useSelector((state) => state)

  // Load main categories on initial component mount
  useEffect(() => {
    dispatch(listCategories(selectedStatus));
  }, [dispatch, selectedStatus]);

  // Handle product loading whenever the filters change
  useEffect(() => {
    if (keyword) {
      dispatch(listProducts({
        keyword: keyword,
        brands: selectedBrands,
        status: selectedStatus,
        ordering: selectedOrdering
      }));
    } else if (id) {
      dispatch(listProducts({
        category: id,
        brands: selectedBrands,
        status: selectedStatus,
        ordering: selectedOrdering
      }));
      dispatch(detailCategories(id, selectedStatus))
    }
  }, [dispatch, id, keyword, selectedBrands, selectedStatus, selectedOrdering]);

  // Handle authentication error
  useEffect(() => {
    if (productsError && productsError.includes('Authentication credentials were not provided')) {
      // Log out the user and redirect to home page
      dispatch(logout());
      navigate('/');
    }
  }, [productsError, dispatch, navigate]);

  const findTopLevelCategory = (categoryData, categoryId) => {
    // If categoryData is not an array but a single object with subCategory
    if (categoryData && !Array.isArray(categoryData) && categoryData.subCategory) {
      return findTopLevelCategory(categoryData.subCategory, categoryId);
    }
    
    // If categoryData is null, undefined, or not an array
    if (!categoryData || !Array.isArray(categoryData)) {
      return null;
    }
    
    // Convert categoryId to number if needed
    const idToFind = typeof categoryId === 'string' ? parseInt(categoryId) : categoryId;
    
    for (const category of categoryData) {
      if (category._id === idToFind) {
        return category;
      } else if (category.subCategory && Array.isArray(category.subCategory)) {
        const result = findTopLevelCategory(category.subCategory, idToFind);
        if (result) {
          return category;
        }
      }
    }
    return null;
  };

  // Find the exact category by ID (not just the top level parent)
  const findExactCategory = (categoryData, categoryId) => {
    if (!categoryData) return null;
    
    // Convert categoryId to number if needed
    const idToFind = typeof categoryId === 'string' ? parseInt(categoryId) : categoryId;
    
    // If categoryData is an object (not array)
    if (!Array.isArray(categoryData)) {
      // Check if this is the category we're looking for
      if (categoryData._id === idToFind) {
        return categoryData;
      }
      
      // Check its subcategories if any
      if (categoryData.subCategory && Array.isArray(categoryData.subCategory)) {
        return findExactCategory(categoryData.subCategory, idToFind);
      }
      
      return null;
    }
    
    // If categoryData is an array
    for (const cat of categoryData) {
      // Check if this is the category we're looking for
      if (cat._id === idToFind) {
        return cat;
      }
      
      // Check subcategories if any
      if (cat.subCategory && Array.isArray(cat.subCategory)) {
        const found = findExactCategory(cat.subCategory, idToFind);
        if (found) return found;
      }
    }
    
    return null;
  };

  // Get status text for display
  const getStatusText = () => {
    switch(selectedStatus) {
      case '1': return 'LIVE';
      case '2': return 'ENDED';
      case '3': return 'UPCOMING';
      default: return 'ALL';
    }
  };

  const copper = COLORS.COPPER;
  const plum = COLORS.PLUM;
  const sageGreen = COLORS.SAGE_GREEN;
  const warmConcrete = COLORS.WARM_CONCRETE;

  const colorPalette = {
    '1': { bg: sageGreen, text: plum },      // Sage Green for LIVE
    '2': { bg: plum, text: warmConcrete },   // Plum for ENDED
    '3': { bg: copper, text: warmConcrete }, // Copper for UPCOMING
    'all': { bg: warmConcrete, text: plum }  // Warm Concrete for ALL
  };

  // Determine what category to display in the sidebar
  let sidebarCategory;
  // For search mode, show all categories
  if (keyword) {
    sidebarCategory = categories;
  } 
  // For category mode, show the parent to maintain navigation
  else if (categories) {
    if (!Array.isArray(categories)) {
      // If categories is a single object, use it directly
      sidebarCategory = categories;
    } else {
      // Find the top level category for the sidebar navigation
      const topLevel = findTopLevelCategory(categories, parseInt(id));
      sidebarCategory = topLevel || categories;
    }
  }

  // Get the actual selected category for display purposes
  const selectedCategory = category || findExactCategory(categories, id);
  
  const orderings = [
    {
        'name': 'Time: Ending soonest',
        'value': 'endDate',
        'id': 'o-1'
    },
    {
        'name': 'Time: Newly listed',
        'value': '-endDate',
        'id': 'o-2'
    },
    {
        'name': 'Bid: Lowest first',
        'value': 'price',
        'id': 'o-3'
    },
    {
        'name': 'Bid: Highest first',
        'value': '-price',
        'id': 'o-4'
    },
    {
        'name': 'Name: A -> Z',
        'value': 'name',
        'id': 'o-5'
    },
    {
        'name': 'Name: Z -> A',
        'value': '-name',
        'id': 'o-6'
    }
  ];

  // Get the category name to display in search results
  const categoryName = keyword 
    ? keyword 
    : (selectedCategory?.name || 'All Categories');

  const isLoading = categoryDetailsLoading || productsLoading;
  
  // Handle products data
  let productsList = [];
  if (products) {
    // Check if products has a results property (paginated response)
    if (products.results && Array.isArray(products.results)) {
      productsList = products.results;
    } 
    // Check if products is directly an array
    else if (Array.isArray(products)) {
      productsList = products;
    }
  }

  return (
    <>
      <FilterContext.Provider value={{ selectedBrands, setSelectedBrands, selectedStatus, setSelectedStatus }}>
      { categoryDetailsError ? 
        <Message variant="danger">{categoryDetailsError}</Message>
       : 
        <Container>
          <Row>
            <Col xs={12} md={3} xl={2}>
              <Row className="p-3 my-3 shadow rounded sticky-top" style={{top :'1rem'}}>
                <Col className="col-12 mb-4 mb-md-0">
                  <CategorySection
                    topLevelCategory={sidebarCategory}
                    searchMode={keyword}
                  />
                </Col>
                <Col>
                  <FilterByStatus />
                </Col>
                <div className='vr mx-3 p-0 d-block d-md-none' style={{width: '0.25px'}} />
                <Col>
                  <FilterByBrand searchParams={keyword} />
                </Col>
              </Row>
            </Col>
            <Col className="ms-md-4 my-3">
              <Row className="flex-column flex-lg-row justify-content-end">
                {isLoading ? '' : 
                  <Col>
                    Listing auction items found in <strong>{categoryName}</strong> - 
                    <strong style={{ 
                      backgroundColor: warmConcrete, 
                      color: plum, 
                      padding: '2px 4px', 
                      borderRadius: '2px',
                      display: 'inline-block',
                      margin: '0 2px',
                      border: `1px solid ${plum}`
                    }}>{getStatusText()}</strong> 
                    found ({productsList.length}) results
                    {productsError && !productsError.includes('Authentication credentials were not provided') && <div className="text-danger mt-2">Error: {productsError}</div>}
                  </Col>
                }
                <Col className="align-self-md-end col-md-6 col-lg-4 col-xl-3">
                  <Form.Select value={selectedOrdering} size="sm" aria-label="ordering" className="shadow-sm fw-semibold my-3 my-md-0" onChange={(e) => setSelectedOrdering(e.target.value)}>
                    {orderings.map((ordering) => (
                      <option
                        key={ordering.id}
                        id={ordering.id}
                        value={ordering.value}
                      >
                        {ordering.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              {isLoading ?
                <Loader />
              :
                <>
                  <Row>
                    {productsList.length > 0 ? (
                      productsList.map((product) => (
                        <Col key={product._id} sm={12} md={6} lg={4} xl={3}>
                          <ProductByCategory product={product} />
                        </Col>
                      ))
                    ) : (
                      <Col className="mt-4">
                        <Alert className="text-center p-2" style={{ backgroundColor: warmConcrete, color: plum, border: `1px solid ${plum}` }}>
                          <FaExclamationTriangle size={14} style={{ marginRight: '6px' }} />
                          <strong>No {getStatusText()} auction items found in this category</strong>
                        </Alert>
                      </Col>
                    )}
                  </Row>
                </>
              }
            </Col>
          </Row>
        </Container>
      }
      </FilterContext.Provider>
    </>
  );
}

export default CategoryScreen;
